import { type ConnectionEvent, configureStore, onDatabaseConnectionChange } from "@nudge-ai/audio-store";
// External dependencies
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ConfigProvider } from "antd";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";

// Internal components
import "./App.css";
import IOSAppBanner from "./components/IOSAppBanner/IOSAppBanner.component";
import LoadingModal from "./components/LoadingModal";
import UpdateNotification from "./components/UpdateNotification/UpdateNotification.component";
import AuthLayout from "./layouts/AuthLayout";
import DefaultLayout from "./layouts/DefaultLayouts";
import UnprotectedHeaderLayout from "./layouts/UnprotectedHeaderLayout";
import Agent from "./pages/agents/Agent";
import AnalyticsPage from "./pages/analytics/analytics.page";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/forget-password.page";
import PasswordResetForm from "./pages/auth/reset-password.page";
import Demo from "./pages/demo/demo.page";
import Baa from "./pages/docs/baa.component";
import Privacy from "./pages/docs/privacy.component";
import Terms from "./pages/docs/terms.component";
import AllNotes from "./pages/home/AllNotes.component";
import ClientNotes from "./pages/home/ClientNotes.component";
import NotesForMobile from "./pages/home/NotesForMobile.component";
import HomePage from "./pages/home/home.page";
import ClinicianProfile from "./pages/members/ClinicianProfile.page";
import Members from "./pages/members/members.page";
import ThankYou from "./pages/payment/ThankYou";
import TemplateDetailsPage from "./pages/templates/template-details.page";
import TemplatesPage from "./pages/templates/templates.page";

// Hooks and utilities
import { useRootPath } from "./hooks/app.hook";
import { useAppDispatch, useAppSelector } from "./hooks/redux-hooks";
import useUserInfo from "./hooks/useUserInfo";
import { patchUserInfo } from "./service/auth.service";
import { fetchUserData } from "./slices/userSlice";
import { theme } from "./theme/ant-theme";
import { initAnalytics } from "./utils/analytics";
import { logAudioStoreConnection } from "./utils/audioStoreLogger";
import { INTERCOM_APP_ID } from "./utils/constants";
import { getBrowserTimezone } from "./utils/timezone";

// Styles
import "./styles/global.scss";
import CallDashboard from "./pages/call/dashboard/call-dashboard.component";
import OnboardingPage from "./pages/onboarding/onboarding.page";

const GOOGLE_CLIENT_ID = "24618415969-sisnrs3hseguen2scue1bdroiton56mo.apps.googleusercontent.com";

function App() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { rootPath } = useRootPath();
  const { fetchUserInfo } = useUserInfo();
  const { userInfo } = useAppSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const hasInitializedRef = useRef(false);

  const onFetchUserInfo = async () => {
    try {
      await fetchUserInfo();

      // Update timezone only if it's different from stored value
      try {
        const currentTimezone = getBrowserTimezone();
        const storedTimezone = userInfo?.onboarding_data?.timezone;

        if (!storedTimezone || currentTimezone !== storedTimezone) {
          console.log(`Updating timezone from '${storedTimezone}' to '${currentTimezone}'`);
          await patchUserInfo({
            onboarding_data: {
              ...userInfo.onboarding_data,
              timezone: currentTimezone,
            },
          });
        }
      } catch (error) {
        console.error("Error updating timezone:", error);
        // Don't block app initialization if timezone update fails
      }

      return;
    } catch (error) {
      console.error("Error fetching user info:", error);
    } finally {
      setLoading(false);
    }
  };

  // Run once on mount to initialize user data
  // biome-ignore lint: this is intentionally mount-only initialization logic
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      dispatch(fetchUserData());
      onFetchUserInfo();
      if (location.pathname === "/" || location.pathname === "/demo") {
        navigate(rootPath);
      }
    } else if (!token) {
      // Remove sessionid cookie if we don't have a token
      // Resolves a bug where on logout we were not properly removing this cookie.
      // Trying to register/login at this point would cause a csrf failure.
      Cookies.remove("sessionid");

      if (
        location &&
        (location.pathname === "/" || location.pathname === "/home" || location.pathname === "/onboarding")
      ) {
        navigate("/register");
      }

      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initAnalytics();
  }, []);

  // Select the audio-backup strategy from the backend rollout flag, before any
  // chunk is written. The flag is passed straight to the package, which decides
  // the engine order internally: true → Cache Storage primary + IndexedDB
  // fallback; false → IndexedDB only (current behaviour). Defensive: the optional
  // local backup must never block app init or the critical recording path.
  useEffect(() => {
    try {
      configureStore(!!userInfo?.enable_cache_storage_backup);
    } catch {
      // ignore — never let backup configuration interfere with the app
    }
  }, [userInfo?.enable_cache_storage_backup]);

  // Bounded triage breadcrumbs for the local audio backup (throttled; never floods).
  useEffect(() => {
    const handleConnectionChange = (event: ConnectionEvent) => {
      try {
        const STATES_TO_LOG = ["closed", "closing", "error", "blocked", "recovered"];
        if (STATES_TO_LOG.includes(event.state)) logAudioStoreConnection(event);
      } catch {
        // logging must never break a storage operation
      }
    };
    try {
      onDatabaseConnectionChange(handleConnectionChange);
    } catch {
      // ignore — connection logging is best-effort
    }
  }, []);

  if (loading) return <LoadingModal />;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ConfigProvider theme={theme}>
        <IntercomProvider appId={INTERCOM_APP_ID}>
          <UpdateNotification showAsModal={true} />
          <IOSAppBanner />
          <Routes>
            <Route element={<DefaultLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<PasswordResetForm />} />
            </Route>
            <Route element={<UnprotectedHeaderLayout />}>
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/baa" element={<Baa />} />
              <Route path="/demo" element={<Demo />} />
            </Route>
            <Route element={<AuthLayout />}>
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/agents" element={<Agent />} />
              <Route path="/home" element={<HomePage />}>
                <Route index element={<AllNotes />} />
                <Route path="clients/:clientUuid" element={<ClientNotes />} />
              </Route>
              <Route path="/members" element={<Members />} />
              <Route path="/members/:userUuid/profile" element={<ClinicianProfile />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/" element={<HomePage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/call/dashboard" element={<CallDashboard />} />
            </Route>
            <Route element={<AuthLayout avoidTokenValidation />}>
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/templates/:slug" element={<TemplateDetailsPage />} />
            </Route>
            <Route path="/notesforMobile" element={<NotesForMobile />}>
              <Route index element={<AllNotes />} />
              <Route path="clients/:clientUuid" element={<ClientNotes />} />
            </Route>
          </Routes>
        </IntercomProvider>
      </ConfigProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
